// A value that follows its input only once the input has held still.
//
// For the inputs of a heavy report: a kind chip, a month stepper or a month
// field changes the URL at once, but every report the server computes from
// statements takes seconds, and the server keeps working out a request the
// browser has given up on. Clicking through four chips should ask once, for
// the fourth, not four times.
//
// The first value is returned at once, so a page's first request is not
// delayed. Values are compared by their JSON, so a query object rebuilt with
// the same contents on every render does not restart the wait.

import { useEffect, useRef, useState } from "react";

/** Long enough to cover a run of clicks, short enough not to read as lag. */
export const REPORT_INPUT_DEBOUNCE_MS = 300;

export function useDebouncedValue<T>(value: T, delayMs: number = REPORT_INPUT_DEBOUNCE_MS): T {
  const [settled, setSettled] = useState(value);
  const settledKey = useRef(JSON.stringify(value));
  const key = JSON.stringify(value);
  const latest = useRef(value);

  useEffect(() => {
    latest.current = value;
  });

  useEffect(() => {
    if (key === settledKey.current) return;
    const timer = setTimeout(() => {
      settledKey.current = key;
      setSettled(latest.current);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [key, delayMs]);

  return settled;
}
